import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Users, Target, Eye, Star, User } from 'lucide-react';
import directorImg from '../assets/director.jpeg';

const Section = ({ children, className = "" }: { children: React.ReactNode, className?: string }) => (
  <section className={`py-16 md:py-24 px-4 ${className}`}>
    <div className="container mx-auto max-w-6xl">
      {children}
    </div>
  </section>
);

const SectionHeader = ({ title, subtitle }: { title: string, subtitle?: string }) => (
  <div className="mb-12">
    <h2 className="text-3xl md:text-4xl font-bold mb-4 gradient-text inline-block relative">
      {title}
      <div className="absolute -bottom-2 left-0 w-1/2 h-1 bg-primary-500 rounded-full"></div>
    </h2>
    {subtitle && <p className="text-lg text-secondary-500 dark:text-secondary-400 mt-4 max-w-2xl">{subtitle}</p>}
  </div>
);

const About = () => {
  return (
    <div className="bg-white dark:bg-secondary-900 min-h-screen">
      {/* Hero Header */}
      <div className="bg-secondary-50 dark:bg-secondary-800 py-16 md:py-20 text-center">
        <div className="container mx-auto px-4">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold mb-6 text-secondary-900 dark:text-white"
          >
            About <span className="text-primary-600 dark:text-primary-400">Devdoot</span>
          </motion.h1>
          <p className="text-xl text-secondary-600 dark:text-secondary-300 max-w-3xl mx-auto">
            A unique blend of self-driven, energetic professionals guided by highly experienced security experts.
          </p>
        </div>
      </div>

      <Section>
        {/* Director's Desk */}
        <div className="flex flex-col lg:flex-row gap-12 items-start mb-20">
          <div className="w-full lg:w-1/3">
            <div className="sticky top-24">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white dark:border-secondary-800">
                <img src={directorImg} alt="Director" className="w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex flex-col justify-end p-6">
                  <h3 className="text-white font-bold text-xl">Rahul Kumar Singh</h3>
                  <span className="text-primary-300 font-medium">Managing Director</span>
                </div>
              </div>
              <div className="mt-4 p-4 bg-secondary-50 dark:bg-secondary-800 rounded-xl">
                <h4 className="font-bold mb-2">Sushil Kant Pandey</h4>
                <span className="text-secondary-500 dark:text-secondary-400 text-sm block">Director</span>
              </div>
            </div>
          </div>

          <div className="w-full lg:w-2/3">
            <SectionHeader title="Director's Desk" />
            <div className="prose dark:prose-invert max-w-none text-lg leading-relaxed text-secondary-600 dark:text-secondary-300 space-y-6">
              <p>
                <strong className="text-primary-600 dark:text-primary-400 text-xl block mb-2">Respected Sir,</strong>
                Devdoot Intelligence & Securities Pvt. Ltd. is a trusted name in the security industry. We are a unique blend of self-driven, young, and energetic team working under the able guidance of highly experienced security experts, providing integrated security solutions to a number of corporate and business establishments.
              </p>
              <p>
                The setup of the company comprises thorough professionals drawn from the Indian Armed Forces and semi-armed forces. We have a team of highly experienced and qualified professionals, along with the best infrastructure, to successfully manage prestigious contracts of any magnitude.
              </p>
              <p>
                Our organization's primary focus is on delivering the best and most accurate results with an emphasis on customer satisfaction. We are best suited to provide you with an alert, efficient, trained, and compact workforce, and we assure you that the standard and quality of our services will meet your requirement and expectation.
              </p>

              <div className="bg-primary-50 dark:bg-primary-900/10 p-6 rounded-xl border border-primary-100 dark:border-primary-800/20 my-8">
                <h4 className="flex items-center gap-2 font-bold text-lg mb-4 text-primary-700 dark:text-primary-400">
                  <User size={24} /> Our Personnel
                </h4>
                <p>
                  Our personnel are, in real terms, professionally trained, knowledgeable, dedicated, courageous, and vigilant — capable of handling any challenging situation at all times. We are also fully equipped and familiar with high-tech security systems and the latest technology.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Mission / Vision Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          <motion.div
            whileHover={{ y: -5 }}
            className="p-8 bg-blue-50 dark:bg-blue-900/10 rounded-2xl border border-blue-100 dark:border-blue-800/20"
          >
            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white mb-6 shadow-lg shadow-blue-500/30">
              <Target size={24} />
            </div>
            <h3 className="text-xl font-bold mb-4 text-blue-900 dark:text-blue-100">Our Mission</h3>
            <p className="text-blue-800/80 dark:text-blue-200/80">
              To provide our honorable client with the highest quality of security & safety services. We shall provide our clients with a full spectrum of systems in the field of security & safety that reflect the most effective potential and loss prevention methods available.
            </p>
          </motion.div>

          <motion.div
            whileHover={{ y: -5 }}
            className="p-8 bg-emerald-50 dark:bg-emerald-900/10 rounded-2xl border border-emerald-100 dark:border-emerald-800/20"
          >
            <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center text-white mb-6 shadow-lg shadow-emerald-500/30">
              <Eye size={24} />
            </div>
            <h3 className="text-xl font-bold mb-4 text-emerald-900 dark:text-emerald-100">Our Vision</h3>
            <p className="text-emerald-800/80 dark:text-emerald-200/80">
              Our vision is to provide our client with best protection and loss prevention services and focus on their specific needs. We will continue to grow and expand our business by maintaining a culture that recognises and rewards innovation through our commitment to excellence.
            </p>
          </motion.div>

          <motion.div
            whileHover={{ y: -5 }}
            className="p-8 bg-amber-50 dark:bg-amber-900/10 rounded-2xl border border-amber-100 dark:border-amber-800/20"
          >
            <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center text-white mb-6 shadow-lg shadow-amber-500/30">
              <Star size={24} />
            </div>
            <h3 className="text-xl font-bold mb-4 text-amber-900 dark:text-amber-100">Special Features</h3>
            <p className="text-amber-800/80 dark:text-amber-200/80">
              DIAS is one of the leading security organizations in India comprising of qualified, highly technical and professionally very sound experts. Controlled and run by a dedicated team of qualified civilians and Armed forces.
            </p>
          </motion.div>
        </div>

        {/* Who We Are */}
        <div className="bg-secondary-900 text-white rounded-3xl p-8 md:p-12 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 p-32 bg-primary-500/20 rounded-full blur-3xl -mr-16 -mt-16"></div>
          <div className="relative z-10 flex flex-col md:flex-row gap-12 items-center">
            <div className="w-full md:w-1/3 text-center">
              <div className="inline-block p-6 rounded-full bg-white/10 backdrop-blur-md mb-6 border border-white/20">
                <Users size={48} className="text-primary-400" />
              </div>
              <h2 className="text-3xl font-bold">Who We Are</h2>
            </div>
            <div className="w-full md:w-2/3 border-l border-white/10 pl-0 md:pl-12">
              <p className="text-lg text-gray-300 mb-6">
                We are a unique blend of self-driven, young, and energetic professionals, guided by highly experienced security experts.
              </p>
              <ul className="space-y-4">
                {[
                  "Trained professionals from the Indian Armed Forces",
                  "A reliable and efficient workforce",
                  "Experts in high-tech security systems",
                  "Focus on customer satisfaction and alertness"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                    <span className="text-gray-200">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default About;